import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { SectorModel } from 'src/app/models/management/sector.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CityTableModel } from 'src/app/models/tables/management/city-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CityService } from 'src/app/services/management/city.service';
import { SectorService } from 'src/app/services/management/sector.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-sector',
  templateUrl: './register-sector.component.html',
  styleUrls: ['./register-sector.component.scss'],
  standalone: false
})
export class RegisterSectorComponent implements OnInit {

  @ViewChild('selectCity') citySelectionModalComponent?: CustomModalComponent | null = null;
  ascendantCity: boolean = true;
  cityId: number = 0;
  city: string = '';
  cityData: PagedResponseModel<CityTableModel> = new PagedResponseModel<CityTableModel>();
  columnSortCity: string | null = null;
  componentCitySelection = CustomTableSelectComponent;
  department: string = '';
  disabledForm: boolean = false;
  filtersCity: ColumnResponseModel[] = [];
  formSector: FormGroup = new FormGroup({});
  headersCity: ColumnResponseModel[] = [];
  inputsForSelectCityModal$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    filters: [],
    showCheckbox: true,
    showRecover: true
  });
  isOpened: string | undefined = undefined;
  limitCity: number = 10;
  nameWindow: string = 'Captura Sectores';
  output$ = new Subject<any>();
  pageCity: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  sectorContract: SectorModel = new SectorModel();
  sectorId: number = 0;
  url: string = 'sector';

  constructor(private confirmDialogService: ConfirmDialogService,
    private activatedRoute: ActivatedRoute,
    private cityService: CityService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private sectorService: SectorService,
    private windowService: WindowService) {
    this.validateForm(new SectorModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.sectorId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeCity(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeCity(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeConsecutive(consecutive: number) {
    if (!this.cityId) {
      return this.toastHandlerService.showToast('warning', 'Seleccione una ciudad.');
    }
    if (!consecutive) {
      this.resetContract();
      return;
    }
    try {
      const response = await firstValueFrom(this.sectorService.getSectorByKeys(this.cityId, consecutive));
      this.sectorId = response.sectorId;
      this.sectorContract = response;
      this.validateForm(this.sectorContract);
      this.location.replaceState(`/${this.url}/register/${this.sectorId}`);
    } catch (error) {
      this.resetContract();
      console.error('Error al obtener el sector.', error);
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear sectores.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.resetContract();
  }

  async createSector(modelSector: SectorModel) {
    try {
      const response = await firstValueFrom(this.sectorService.createSector(modelSector));
      this.sectorId = response.sectorId;
      this.sectorContract = response;
      this.validateForm(this.sectorContract);
      this.location.replaceState(`/${this.url}/register/${this.sectorId}`);
      this.toastHandlerService.showToast('success', `El sector fue creado exitosamente.`);
    } catch (error) {
      console.error('Error al crear el sector.', error);
    }
  }

  async deleteSector() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar sectores.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (!this.sectorId) {
      this.toastHandlerService.showToast('warning', 'Debe crear un sector.');
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el sector?')) {
      try {
        await firstValueFrom(this.sectorService.deleteSector(this.sectorId));
        this.resetContract();
        this.toastHandlerService.showToast('success', 'El sector fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el sector.', error);
      }
    }
  }

  async getButtonClick(buttonClick: any) {
    if (!this.cityId) {
      return this.toastHandlerService.showToast('warning', 'Seleccione una ciudad.');
    }
    await this.handleSectorNavigation(buttonClick, this.cityId)
  }

  async getCityDepartment(cityId: number) {
    try {
      const response: any = await firstValueFrom(this.cityService.getCityDepartment(cityId));
      this.city = response.city;
      this.cityId = response.cityId;
      this.department = response.department;
    } catch (error) {
      console.error('Error al obtener la ciudad y el departamento.', error);
    }
  }

  async getColumnsForCitiesSelection() {
    try {
      if (this.headersCity.length === 0) {
        this.headersCity = await firstValueFrom(this.cityService.getColumnsForCitySelection());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para el modal de ciudades.', error);
    }
  }

  async getCitiesForSelection() {
    try {
      this.cityData = await firstValueFrom(this.cityService.getCitiesForSelection(this.pageCity, this.limitCity,
        this.columnSortCity, this.ascendantCity, this.filtersCity));
      this.updateInputsForSelectCityModal();
    } catch (error) {
      console.error('Error al obtener las ciudades para el modal.', error);
    }
  }

  async getSectorById() {
    try {
      const response = await firstValueFrom(this.sectorService.getSectorById(this.sectorId));
      this.sectorContract = response;
      this.validateForm(this.sectorContract);
      this.location.replaceState(`/${this.url}/register/${this.sectorId}`);
    } catch (error) {
      console.error('Error al obtener el sector por id.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async handleInitialState() {
    if (this.sectorId) {
      await this.getSectorById();
    } else {
      this.createNewRegister();
    }
  }

  async handleSectorNavigation(buttonClick: any, cityId: number) {
    const consecutive = this.formSector.get("consecutive")?.value;
    const actionMap: Record<string, () => Promise<SectorModel>> = {
      first: () =>
        firstValueFrom(this.sectorService.getFirstSectorByKeys(cityId)),
      previus: () =>
        firstValueFrom(this.sectorService.getPreviusSectorByKeys(cityId, consecutive)),
      next: () =>
        firstValueFrom(this.sectorService.getNextSectorByKeys(cityId, consecutive)),
      last: () =>
        firstValueFrom(this.sectorService.getLastSectorByKeys(cityId)),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const sector = await action();
      this.sectorId = sector.sectorId;
      this.sectorContract = sector;
      this.validateForm(this.sectorContract);
      this.location.replaceState(`/${this.url}/register/${this.sectorId}`);
    } catch (error) {
      console.error(`Error al obtener el sector (${buttonClick.target.id}).`, error);
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersCity = this.filtersCity.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersCity.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async setCityDepartment(selectedItem: any) {
    const [city] = selectedItem;
    this.cityId = city.id
    this.sectorContract = new SectorModel();
    this.sectorContract.cityId = city.id;
    this.sectorContract.departmentId = city.departmentId;
    this.validateForm(this.sectorContract);
    this.city = `${city.code} - ${city.name}`;
    this.department = `${city.departmentCode} - ${city.departmentName}`;
  }

  async send() {
    this.validateFormControls();
    if (!this.formSector.valid) {
      this.showValidationMessages();
      return;
    }
    const modelSector = this.formSector.getRawValue() as SectorModel;
    if (this.sectorId == 0) {
      await this.createSector(modelSector);
    } else {
      await this.updateSector(modelSector);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      consecutive: {
        required: 'El campo Consecutivo es obligatorio.',
        maxlength: 'El Consecutivo no puede tener más de 3 caracteres.'
      },
      departmentId: {
        required: 'Debe seleccionar un Departamento.',
        min: 'Debe seleccionar un Departamento válido.'
      },
      cityId: {
        required: 'Debe seleccionar una Ciudad.',
        min: 'Debe seleccionar una Ciudad válida.'
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede tener más de 50 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formSector.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async resetContract() {
    this.cityId = 0;
    this.sectorId = 0;
    this.city = '';
    this.department = '';
    this.sectorContract = new SectorModel();
    this.validateForm(this.sectorContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async openCitySelectionModal() {
    try {
      this.filtersCity = [];
      this.updateInputsForSelectCityModal();
      await Promise.all([
        this.getColumnsForCitiesSelection(),
        this.getCitiesForSelection()
      ]);
      this.citySelectionModalComponent?.show();
    } catch (error) {
      console.error('Error al abrir el modal de selección de ciudad.', error);
    }
  }

  async onFilterChangeCity(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersCity = this.removeDuplicates(filters);
    this.filtersCity.forEach((filter: any) => this.processFilter(filter));
    this.updateInputsForSelectCityModal();
  }

  async onPageChangeCity(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageCity = event.page;
    this.limitCity = event.pageSize;
    this.columnSortCity = event.sortField || null;
    this.ascendantCity = event.sortOrder === 1;
    await this.getCitiesForSelection();
  }

  updateInputsForSelectCityModal() {
    this.inputsForSelectCityModal$.next({
      headers: this.headersCity,
      data: this.cityData,
      filters: this.filtersCity,
      showCheckbox: true,
      showRecover: true
    });
  }

  updateFormPermision() {
    const controlConsecutive = this.formSector.controls['consecutive'];
    setTimeout(() => {
      if (this.permissionUpdatable) {
        this.disabledForm = false;
        this.formSector.enable();
        this.sectorId ? controlConsecutive.enable() : controlConsecutive.disable();
      } else {
        this.disabledForm = true;
        this.formSector.disable();
      }
    });
  }

  async updateSector(modelSector: SectorModel) {
    this.sectorContract.name = modelSector.name
    try {
      const response = await firstValueFrom(this.sectorService.updateSector(this.sectorContract));
      this.sectorContract = response;
      this.validateForm(this.sectorContract);
      this.toastHandlerService.showToast('success', `El sector fue modificado exitosamente.`);
    } catch (error) {
      console.error('Error al actualizar el sector.', error);
    }
  }

  async validateForm(sector: SectorModel) {
    this.formSector = this.formBuilder.group({
      consecutive: [sector.consecutive, [Validators.required, Validators.maxLength(3)]],
      departmentId: [sector.departmentId, [Validators.required, Validators.min(1)]],
      cityId: [sector.cityId, [Validators.required, Validators.min(1)]],
      name: [sector.name, [Validators.required, Validators.maxLength(50)]]
    });
    if (sector.sectorId && sector.cityId) { await this.getCityDepartment(sector.cityId); }
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formSector.controls).forEach((key) => {
      const control = this.formSector.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
